import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
  styled,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DownloadIcon from "@mui/icons-material/Download";
import exportFromJSON from "export-from-json";
import { getInvoiceSummaryList, getInvoiceDetailsByInvoiceId, updateInvoiceItemSkuByInvoiceItemId } from "../services/InvoiceService";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import JBWDateLabel from "./JBWDateLabel";
import { FaEdit, FaSave } from "react-icons/fa";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "left",
  color: theme.palette.text.secondary,
}));

const InvoiceDetailDialog = ({ invoiceId, dialogOpen, onClose }) => {
  const [invoice, setInvoice] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editedValue, setEditedValue] = useState("");

  const handleSKUUpdateSave = async (id) => {
    try {
      await updateInvoiceItemSkuByInvoiceItemId(id, editedValue); // API call

      // refresh invoice data
      await loadInvoiceDetails();

      // exit edit mode
      setEditingId(null);
      setEditedValue("");
    } catch (err) {
      console.error("Error updating SKU", err);
    }
  };

  const loadInvoiceDetails = async () => {
    if (!invoiceId) return;
    setInvoice(null);
    try {
      const res = await getInvoiceDetailsByInvoiceId(invoiceId);
      const data = res.data;
      if (res.status === 200 && data && data.length > 0) {
        setInvoice(data[0]); // set the updated invoice data
      }
    } catch (err) {
      console.error("Get Invoice Internal Server error", err);
    }
  };

  useEffect(() => {
    loadInvoiceDetails();
  }, [invoiceId]);

  const handleClose = (_event, reason) => {
    if (reason === "escapeKeyDown" || reason === "backdropClick") return;
    setInvoice(null);
    onClose();
  };

  return (
    <Dialog
      open={dialogOpen}
      disableEscapeKeyDown
      fullWidth
      maxWidth="lg"
      PaperProps={{ component: "div" }}
    >
      <DialogTitle>Invoice Details</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        {invoice === null ? (
          <Box sx={{ height: "100px" }}>
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Item>
              <TableContainer>
                <Table sx={{ minWidth: 550 }} size="small" aria-label="Invoice table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice Id</TableCell>
                      <TableCell align="left">Customer Name</TableCell>
                      <TableCell align="left">Phone &amp; Email</TableCell>
                      <TableCell align="left">Address</TableCell>
                      <TableCell align="left">Invoice Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row">{invoice?.invoiceId}</TableCell>
                      <TableCell align="left">{invoice?.customerName}</TableCell>
                      <TableCell align="left">
                        <Typography variant="subtitle1" component="span" sx={{ wordWrap: "break-word" }} whiteSpace="normal">
                          {invoice?.phoneNumber}
                        </Typography>
                        <br />
                        <Typography variant="subtitle1" component="span" sx={{ wordWrap: "break-word" }} whiteSpace="normal">
                          {invoice?.emailAddress}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="subtitle1" component="span" sx={{ wordWrap: "break-word" }} whiteSpace="normal">
                          {invoice?.location}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        {invoice?.serviceStartDate && (
                          <JBWDateLabel inputDate={invoice.serviceStartDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Item>
            <Item>
              <Stack>
                <Item elevation={0}>
                  <Typography variant="subtitle1" component="h2" sx={{ fontWeight: "bold" }}>
                    Invoice Items:
                  </Typography>
                </Item>
                <Item elevation={0}>
                  {invoice.invoiceItems && invoice.invoiceItems.length > 0 ? (
                    <TableContainer>
                      <Table sx={{ minWidth: 550 }} size="small" aria-label="Invoice Items table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item SKU Name</TableCell>
                            <TableCell align="left">Item Display Name</TableCell>
                            <TableCell align="left">Quantity</TableCell>
                            <TableCell align="left">Manufacturer</TableCell>
                            <TableCell align="left">Model No</TableCell>
                            <TableCell align="left">Serial Number</TableCell>
                            <TableCell align="left">Total Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoice.invoiceItems.map((item) => (
                            <TableRow key={item.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                              {/* EDITABLE COLUMN */}
                              <TableCell component="th" scope="row" sx={{ minWidth: 150 }}>
                                {editingId === item.id ? (() => {
                                  const isChanged = editedValue !== (item?.itemSKUName || "");

                                  return (
                                    <Box display="flex" alignItems="center" gap={1} width="100%">
                                      <TextField
                                        value={editedValue}
                                        onChange={(e) => setEditedValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" && isChanged) {
                                            handleSKUUpdateSave(item.id);
                                          }
                                          if (e.key === "Escape") {
                                            setEditingId(null);
                                          }
                                        }}
                                        autoFocus
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          width: 160,
                                          "& .MuiInputBase-input": {
                                            padding: "6px 8px",
                                            fontSize: "13px",
                                          },
                                        }}
                                      />

                                      <FaSave
                                        style={{
                                          cursor: isChanged ? "pointer" : "not-allowed",
                                          color: isChanged ? "green" : "grey",
                                          opacity: isChanged ? 1 : 0.5,
                                        }}
                                        onClick={() => {
                                          if (isChanged) {
                                            handleSKUUpdateSave(item.id);
                                          }
                                        }}
                                      />
                                    </Box>
                                  );
                                })() : (
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <span>{item?.itemSKUName}</span>

                                    <FaEdit
                                      style={{ cursor: "pointer", color: "#1976d2" }}
                                      onClick={() => {
                                        setEditingId(item.id);
                                        setEditedValue(item.itemSKUName || "");
                                      }}
                                    />
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell align="left">{item?.itemDisplayName}</TableCell>
                              <TableCell align="left">{item?.quantity}</TableCell>
                              <TableCell align="left">{item?.manufacturer}</TableCell>
                              <TableCell align="left">{item?.modelNo}</TableCell>
                              <TableCell align="left">{item?.serialNumber}</TableCell>
                              <TableCell align="left">{item?.totalCost}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="subtitle1" component="h6" sx={{ fontWeight: "bold" }}>
                      NO Invoice Items
                    </Typography>
                  )}
                </Item>
              </Stack>
            </Item>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InvoiceSummary = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [_infoMsg, setInfoMsg] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getInvoiceSummaryList()
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setRows(res.data);
          setInfoMsg(null);
        } else {
          setInfoMsg({ msg: "No Invoices Found", colorCode: "#B8001F" });
        }
      })
      .catch(() => {
        setInfoMsg({ msg: "Error loading invoice summary", colorCode: "#B8001F" });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleExportCsv = () => {
    const exportData = rows.map((row) => {
      const { addressLine1, addressLine2, city, state, zipcode } = row;
      const parts = [
        addressLine1,
        addressLine2 && addressLine2 !== "null" ? addressLine2 : null,
        city && city !== "null" ? city : null,
        state && state !== "null" ? state : null,
        zipcode && zipcode !== "null" ? zipcode : null,
      ].filter(Boolean);
      return {
        "Invoice #": row.invoiceId,
        "Status": row.invoiceStatus,
        "Summary": row.invoiceSummary,
        "Job ID": row.jobNumber,
        "Customer Name": `${row.firstName || ""} ${row.lastName || ""}`.trim(),
        "Address": parts.join(", "),
        "ST Created On": row.stCreatedOn ? new Date(row.stCreatedOn).toLocaleDateString() : "",
        "ST Modified On": row.stModifiedOn ? new Date(row.stModifiedOn).toLocaleDateString() : "",
      };
    });
    exportFromJSON({
      data: exportData,
      fileName: `invoice_scanner_${Date.now()}`,
      exportType: exportFromJSON.types.csv,
    });
  };

  const handleViewDetails = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setDialogOpen(true);
  };

  const columns = useMemo(() => [
    {
      field: "invoiceId",
      headerName: "Invoice #",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "invoiceStatus",
      headerName: "Status",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "invoiceSummary",
      headerName: "Summary",
      width: 250,
      sortable: false,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "jobNumber",
      headerName: "Job ID",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "firstName",
      headerName: "Customer Name",
      width: 160,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => `${param.row.firstName} ${param.row.lastName}`,
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 220,
      sortable: false,
      headerAlign: "center",
      align: "left",
      renderCell: (param) => {
        const { addressLine1, addressLine2, city, state, zipcode } = param.row;
        if (!addressLine1) return "";
        const parts = [
          addressLine1,
          addressLine2 && addressLine2 !== "null" ? addressLine2 : null,
          city && city !== "null" ? city : null,
          state && state !== "null" ? state : null,
          zipcode && zipcode !== "null" ? zipcode : null,
        ].filter(Boolean);
        return (
          <Typography variant="body2" sx={{ wordWrap: "break-word", whiteSpace: "normal" }}>
            {parts.join(", ")}
          </Typography>
        );
      },
    },
    {
      field: "stCreatedOn",
      headerName: "ST Created On",
      sortable: false,
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        if (param.value === null) return "";
        return <JBWDateLabel inputDate={param.value} dateFormat="MMM.DD,YYYY" />;
      },
    },
    {
      field: "stModifiedOn",
      headerName: "ST Modified On",
      sortable: false,
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        if (param.value === null) return "";
        return <JBWDateLabel inputDate={param.value} dateFormat="MMM.DD,YYYY" />;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => (
        <Tooltip title="View Invoice Details">
          <IconButton size="small" onClick={() => handleViewDetails(param.row.invoiceId)}>
            <ReceiptIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Tooltip title="Export to CSV">
          <span>
            <IconButton
              color="success"
              onClick={handleExportCsv}
              disabled={rows.length === 0}
              sx={{ border: "1px solid #ccc" }}
            >
              <DownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ height: 500 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          getRowId={(row) => `${row.invoiceId}-${row.dealerId}`}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>
      <InvoiceDetailDialog
        invoiceId={selectedInvoiceId}
        dialogOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default InvoiceSummary;
